import { makeStyles } from '@material-ui/styles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { v4 as uuid } from 'uuid'
import { maintenanceModeDoc, vtpmMigrationDoc } from 'app/docLinks'
import StatsCell from 'app/plugins/infrastructure/components/common/cells/StatsCell'
import {
  enableVolumeMaintenance,
  listStorageServiceDetails,
} from 'app/plugins/infrastructure/components/common/service-health/action'
import { allStorageServicesSelector } from 'app/plugins/infrastructure/components/common/service-health/selector'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import ExternalLink from 'core/components/ExternalLink'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import SimpleLink from 'core/components/SimpleLink'
import PicklistField from 'core/components/validatedForm/DropdownField'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Info from 'core/components/validatedForm/Info'
import ListTableField from 'core/components/validatedForm/ListTableField'
import RadioFields from 'core/components/validatedForm/radio-fields'
import ToggleSwitchField from 'core/components/validatedForm/ToggleSwitchField'
import Badge from 'core/elements/badge/Badge'
import Card from 'core/elements/card'
import AsyncDropdown from 'core/elements/dropdown/AsyncDropdown'
import Grid from 'core/elements/grid/Grid'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import type Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { listImages } from 'openstack/components/images/actions'
import { simpleAddVmTag, simpleMigrateVirtualMachine } from 'openstack/components/vms/actions'
import {
  VALID_COLD_MIGRATION_STATES,
  VALID_MIGRATION_STATES,
} from 'openstack/components/vms/MigrateVirtualMachineDialog'
import { IVmDetailsPageTabs } from 'openstack/components/vms/vm-details/model'
import { getAllVolumesWithParams } from '../../storage/volumes/actions'
import { volumeColumns } from '../../storage/volumes/HelperComponents'
import {
  enableMaintenanceMode,
  getVmsForHost,
  initiateVolumeHostTransfer,
  listHypervisors,
  listResmgrHosts,
} from '../actions'
import { activeRoleHostsByCluster } from '../clusters/selectors'
import { hypervisorsSelector, resmgrHostsSelector, UNASSIGNED_CLUSTER } from '../selectors'
import { IPAddressCellComponent, RoleStatusesCell } from './HelperComponents'

const getBadgeVariant = (status) =>
  ({
    active: 'success',
    paused: 'warning',
    stopped: 'error',
    error: 'error',
    suspended: 'warning',
    building: 'success',
    rescued: 'warning',
  })[status] || 'unknown'

const getBadgeText = (status, pausedPowerState, taskState) => {
  const text =
    {
      active: 'Active',
      paused: 'Paused',
      stopped: 'Stopped',
      error: 'Error',
      suspended: 'Suspended',
      building: 'Building',
      rescued: 'Rescued',
    }[status] || status
  return taskState ? `${text} (${taskState})` : pausedPowerState ? `${text} (paused)` : text
}

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: theme.spacing(2),
    display: 'grid',
  },
  // RadioFields ignores `orientation` when a className is passed, and withFormContext always
  // passes one, so the layout is set here (options side by side).
  migrationOptions: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    alignItems: 'center',
    gridGap: theme.spacing(2),
  },
  migrations: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(2),
  },
  migrationHeader: {
    textAlign: 'center',
  },
  migratingVms: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr 1fr 1fr',
    gap: '8px 32px',
    paddingBottom: theme.spacing(2),
  },
  centered: {
    textAlign: 'center',
  },
  switchTooltip: {
    padding: theme.spacing(1),
    color: theme.palette.common.white,
  },
  errorIcon: {
    marginRight: 4,
  },
  resultCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  resultPass: {
    color: theme.palette.green[500],
  },
  resultFail: {
    color: theme.palette.red[500],
  },
  tabbed: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
    display: 'grid',
    gap: 4,
  },
  horizontalScroll: {
    overflow: 'scroll',
  },
  backendsTable: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    gap: theme.spacing(1),
  },
  // One backend's cells as a subgrid so its columns line up with the header and other
  // backends, with a line above it (above the first backend, and between backends).
  backendRow: {
    gridColumn: '1 / -1',
    display: 'grid',
    gridTemplateColumns: 'subgrid',
    gap: 8,
    paddingTop: 8,
    borderTop: `1px solid ${theme.components.table.border}`,
  },
  backend: {
    display: 'grid',
    gap: 4,
    justifyItems: 'start',
  },
  volumeList: {
    // Span the whole row of the backends grid
    gridColumn: '1 / -1',
    overflow: 'auto',
    marginTop: theme.spacing(1),
    // Same border color/width the Grid uses for its container (components.table.border)
    borderLeft: `1px solid ${theme.components.table.border}`,
    borderRight: `1px solid ${theme.components.table.border}`,
  },
  hostCode: {
    fontFamily: 'SpaceMono',
    fontSize: 13,
    wordBreak: 'break-all',
  },
  verticalCenter: {
    alignSelf: 'center',
  },
}))

interface Props {
  rows: any[]
  onClose: (success?: boolean) => void
}

const hostColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'roleStatuses',
    label: 'Role Status',
    render: (value, item) => <RoleStatusesCell value={value} item={item} />,
  },
  {
    key: 'responding',
    label: 'Connection Status',
  },
  {
    id: 'interfaces',
    label: 'IP Address',
    render: (value) => <IPAddressCellComponent value={value} />,
  },
  {
    id: 'clusterName',
    label: 'cluster',
  },
  {
    id: 'usage',
    label: 'Resource Utilization',
    render: (value) => <StatsCell value={value} />,
  },
  {
    id: 'displayRoles',
    label: 'Assigned Roles',
    render: (values) => values.join(', '),
  },
]

// EMM-FR-006: Volume grid columns for the expandable per-backend volume list
// Reuse the Volumes list page's cell renderers so these rows look the same as there
const VOLUME_LIST_COLUMN_KEYS = ['name', 'status', 'size']
export const volumeListColumns = volumeColumns({
  // volumeColumns calls isAdmin() to decide on admin-only columns, which are filtered out below
  isAdmin: () => false,
  allTenants: false,
  setErrorVolume: null,
}).filter((column) => VOLUME_LIST_COLUMN_KEYS.includes(column.key as string))

// Outcome of the dry-run volume host transfer for a volume, set on the row as `transferResult`
export interface VolumeTransferResult {
  loading?: boolean
  transferable?: boolean
  reason?: string
  error?: string
}

// EMM-FR-007: "Result" cell showing whether the volume would transfer (circle-check) or is
// blocked (circle-xmark with the reason)
const TransferResultCell = ({ item }) => {
  const classes = useStyles()
  const result: VolumeTransferResult | undefined = item?.transferResult
  if (!result) return null
  if (result.loading) return <Text variant="body2">Checking...</Text>
  if (result.error) {
    return (
      <div className={classes.resultCell}>
        <FontAwesomeIcon className={classes.resultFail} solid>
          circle-xmark
        </FontAwesomeIcon>
        <Text variant="body2">{result.error}</Text>
      </div>
    )
  }
  return result.transferable ? (
    <div className={classes.resultCell}>
      <FontAwesomeIcon className={classes.resultPass} solid>
        circle-check
      </FontAwesomeIcon>
    </div>
  ) : (
    <div className={classes.resultCell}>
      <FontAwesomeIcon className={classes.resultFail} solid>
        circle-xmark
      </FontAwesomeIcon>
      <Text variant="body2">{result.reason}</Text>
    </div>
  )
}

export const volumeListColumnsWithResult = [
  ...volumeListColumns,
  {
    key: 'transferResult',
    label: 'Transferable',
    CellComponent: TransferResultCell,
    disableSorting: true,
  },
]

// Dry-run state for one source backend: in flight, failed, or the result per volume id
export interface DryRunState {
  loading?: boolean
  error?: string
  byVolumeId?: Record<string, VolumeTransferResult>
}

// Dry-run volume host transfer from one backend service to another
export const runVolumeTransferDryRun = async (
  sourceHostId: string,
  targetHostId: string,
): Promise<DryRunState> => {
  try {
    const response = await initiateVolumeHostTransfer({ sourceHostId, targetHostId, dryRun: true })
    const volumes = response?.volume_host_transfer?.volumes
    if (!volumes) {
      return { error: 'Unable to check transfer' }
    }
    const byVolumeId: Record<string, VolumeTransferResult> = {}
    volumes.transferable?.forEach((volume) => {
      byVolumeId[volume.id] = { transferable: true }
    })
    volumes.blocked?.forEach((volume) => {
      byVolumeId[volume.id] = { transferable: false, reason: volume.reason }
    })
    return { byVolumeId }
  } catch (e) {
    return { error: e?.message || 'Unable to check transfer' }
  }
}

// Attach a backend's dry-run state to its volumes as `transferResult` for the Result column
export const attachTransferResults = (volumes: any[] = [], dryRun?: DryRunState) =>
  volumes.map((volume) => ({
    ...volume,
    transferResult: dryRun?.loading
      ? { loading: true }
      : dryRun?.error
        ? { error: dryRun.error }
        : dryRun?.byVolumeId?.[volume.id],
  }))

enum MigrationOptions {
  VmsAndStorage = 'vmsAndStorage',
  VmsOnly = 'vmsOnly',
  StorageOnly = 'storageOnly',
}

// EMM-FR-001: Default migrate option derived from the host's roles
const getDefaultMigrationOption = ({ isHypervisor, isStorageNode, isHypervisorAndStorageNode }) => {
  if (isHypervisorAndStorageNode) {
    return MigrationOptions.VmsAndStorage
  } else if (isHypervisor) {
    return MigrationOptions.VmsOnly
  } else if (isStorageNode) {
    return MigrationOptions.StorageOnly
  }
  return null
}

// EMM-FR-001: Migrate options offered for the host's roles
const getMigrationOptions = ({ isHypervisor, isStorageNode, isHypervisorAndStorageNode }) => {
  if (isHypervisorAndStorageNode) {
    return [
      {
        value: MigrationOptions.VmsAndStorage,
        label: 'VMs and Volumes',
      },
      {
        value: MigrationOptions.VmsOnly,
        label: 'VMs Only',
      },
      {
        value: MigrationOptions.StorageOnly,
        label: 'Volumes Only',
      },
    ]
  } else if (isHypervisor) {
    return [
      {
        value: MigrationOptions.VmsOnly,
        label: 'VMs Only',
      },
    ]
  } else if (isStorageNode) {
    return [
      {
        value: MigrationOptions.StorageOnly,
        label: 'Volumes Only',
      },
    ]
  }
  return []
}

// See EnableMaintenanceModeDialog.spec.md for EMM-FR-001 through EMM-FR-013
export default function EnableMaintenanceModeDialog({ rows: hosts, onClose }: Props) {
  const host = hosts?.[0]
  const classes = useStyles()
  const { history } = useReactRouter()
  const isHypervisor = host?.roles?.includes('hypervisor')
  // Only hypervisors load VMs, so a storage-only host must not start in the loading state
  const [loadingVms, setLoadingVms] = useState(!!isHypervisor)
  const [vmsOnHost, setVmsOnHost] = useState([])
  const [volumesByCinderService, setVolumesByCinderService] = useState({})
  // Source service hosts whose volume list is expanded in the backends table
  const [expandedBackends, setExpandedBackends] = useState<Record<string, boolean>>({})
  // Dry-run transfer outcome per source service host: loading, error, or volume id -> result
  const [dryRunResults, setDryRunResults] = useState<Record<string, DryRunState>>({})
  // Latest target requested per source service host, to drop stale dry-run responses
  const latestDryRunTarget = useRef<Record<string, string>>({})
  const [submittingMigrations, setSubmittingMigrations] = useState(false)
  const [submittingStorage, setSubmittingStorage] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)

  const isStorageNode = host?.roles?.includes('persistent-storage')
  const isHypervisorAndStorageNode = isHypervisor && isStorageNode

  const roleHostsByCluster = useSelector(activeRoleHostsByCluster)
  const isLastHypervisor = useMemo(() => {
    const clusterHypervisors = roleHostsByCluster?.[host?.clusterName]?.hypervisor
    return clusterHypervisors?.length === 1 && clusterHypervisors?.[0]?.id === host?.id
  }, [roleHostsByCluster, host])

  // EMM-FR-002: Load VMs for hypervisors only
  const reloadVmsOnHost = useCallback(() => {
    const makeCalls = async () => {
      const vms = await getVmsForHost(host.id)
      setVmsOnHost(vms)
      setLoadingVms(false)
    }
    makeCalls()
  }, [host])

  const { loading: loadingImages } = useListAction(listImages)

  useEffect(() => {
    if (!isHypervisor) {
      return
    }
    // Get initial number of VMs on the host to determine
    // if migrations are necessary
    reloadVmsOnHost()
  }, [isHypervisor])

  const defaultParams = {
    selectHost: false,
    skipPoweredOffVms: false,
    host: [],
    // Target service host (host@backend) to transfer to, keyed by the source service host
    transferTargets: {},
    migrationOption: getDefaultMigrationOption({
      isHypervisor,
      isStorageNode,
      isHypervisorAndStorageNode,
    }),
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const {
    message,
    loading: loadingResmgrHosts,
    reload,
  } = useListAction(listResmgrHosts, {
    params,
  })

  const { loading: loadingStorageServices } = useListAction(listStorageServiceDetails)
  const storageServices = useSelector(allStorageServicesSelector)

  const allHosts = useSelector(resmgrHostsSelector)
  const { loading: loadingHypervisors, reload: reloadHypervisors } = useListAction(listHypervisors)
  const hypervisors = useSelector(hypervisorsSelector)

  // EMM-FR-005: Candidate hosts for designating a VM migration target
  const validHosts = useMemo(() => {
    if (!allHosts.length || !host || !hypervisors.length) {
      return []
    }
    const hypervisorsByResmgrId = hypervisors.reduce((accum, hypervisor) => {
      return {
        ...accum,
        [hypervisor?.service?.host]: hypervisor,
      }
    }, {})
    const hostsWithHypervisor = allHosts.map((host) => {
      return {
        ...host,
        hypervisor: hypervisorsByResmgrId[host.id],
      }
    })

    return hostsWithHypervisor.filter((resmgrHost) => {
      return (
        resmgrHost.id !== host.id &&
        resmgrHost.hypervisor?.state === 'up' &&
        resmgrHost.hypervisor?.status === 'enabled' &&
        // If the host is unassigned, then it can be moved to any cluster
        // If the host is assigned to a cluster, then it can only be moved to host with the same cluster or unassigned host
        (host?.clusterName === UNASSIGNED_CLUSTER ||
          resmgrHost?.clusterName === host?.clusterName ||
          resmgrHost?.clusterName === UNASSIGNED_CLUSTER)
      )
    })
  }, [allHosts, host, hypervisors])

  const storageServicesForHost = useMemo(() => {
    return storageServices?.filter((service) => service?.host.includes(host?.id))
  }, [storageServices, host])

  // EMM-FR-007: Transfer target options per backend
  // Other hosts running the same storage backend as this service (matched on
  // volumeBackend), excluding the host being put into maintenance.
  const getTransferTargetOptions = useCallback(
    (service) =>
      (storageServices || [])
        .filter(
          (candidate) =>
            candidate?.volumeBackend === service?.volumeBackend &&
            candidate?.schedulingStatus === 'enabled' &&
            !candidate?.host?.includes(host?.id),
        )
        .map((candidate) => ({
          label:
            candidate?.hostDetails?.name ||
            candidate?.hostDetails?.info?.hostname ||
            candidate?.host,
          value: candidate?.host,
        })),
    [storageServices, host],
  )

  // EMM-FR-007: Choosing a transfer target runs a dry-run volume host transfer for that
  // source -> target pair and records which volumes would move or are blocked (and why)
  const handleTransferTargetChange = useCallback(
    async (service, targetHostId) => {
      const sourceHostId = service?.host
      updateParams({
        transferTargets: { ...params.transferTargets, [sourceHostId]: targetHostId },
      })
      latestDryRunTarget.current[sourceHostId] = targetHostId
      // Expand the volume list so the dry-run result is visible right away, if there are volumes
      if (volumesByCinderService[sourceHostId]?.length) {
        setExpandedBackends((prev) => ({ ...prev, [sourceHostId]: true }))
      }
      setDryRunResults((prev) => ({ ...prev, [sourceHostId]: { loading: true } }))
      const result = await runVolumeTransferDryRun(sourceHostId, targetHostId)
      // The target changed while this dry run was in flight
      if (latestDryRunTarget.current[sourceHostId] !== targetHostId) return
      setDryRunResults((prev) => ({ ...prev, [sourceHostId]: result }))
    },
    [params.transferTargets, updateParams, volumesByCinderService],
  )

  // Volumes of a backend with their dry-run result attached for the Result column
  const getVolumesWithResult = (service) =>
    attachTransferResults(volumesByCinderService[service?.host], dryRunResults[service?.host])

  // EMM-FR-006: Fetch all volumes (no limit) for this host and group them by backend
  useEffect(() => {
    if (storageServicesForHost?.length) {
      // Need to get volumes list for host and display per backend, does the API support this?
      const makeStorageCall = async () => {
        const allVolumes = await getAllVolumesWithParams({ filters: { host_id: host?.id } })
        const volumesByService = allVolumes.reduce((accum, volume) => {
          const volumeBackend = volume?.['os-vol-host-attr:host']?.split('#')?.[0]
          if (accum[volumeBackend]) {
            accum[volumeBackend].push(volume)
          } else {
            accum[volumeBackend] = [volume]
          }
          return accum
        }, {})
        setVolumesByCinderService(volumesByService)
      }
      makeStorageCall()
    }
  }, [storageServicesForHost, host])

  const {
    update: disableHypervisor,
    updating: disablingHypervisor,
    error: errorDisablingHypervisor,
    reset: resetDisableHypervisor,
  } = useUpdateAction(enableMaintenanceMode)

  const {
    update: disableStorage,
    updating: disablingStorage,
    error: errorDisablingStorage,
    reset: resetDisableStorage,
  } = useUpdateAction(enableVolumeMaintenance)

  const handleMaintenanceToggle = useCallback(
    async (migrationId) => {
      const timestamp = Date.now()
      const body = {
        host: host.id,
        binary: 'nova-compute',
        disabled_reason: `${migrationId}:${timestamp}`,
      }
      await disableHypervisor({ id: host.hypervisor.id, body })

      // Close the dialog if no VMs on the host
      if (!vmsOnHost.length) {
        handleClose(true)
      }
    },
    [params, host, vmsOnHost.length],
  )

  const handleVolumeMaintenanceToggle = useCallback(
    async (serviceId) => {
      const timestamp = Date.now()
      const body = {
        host: serviceId,
        binary: 'cinder-volume',
        disabled_reason: `maintenance:${timestamp}`,
      }
      return await disableStorage({ body })
    },
    [params, host],
  )

  // EMM-FR-008: Disable each storage service, then transfer its volumes
  // Put every storage service on this host into maintenance, then hand its volumes
  // to the target service chosen for that backend.
  const handleStorageMaintenance = useCallback(async () => {
    setSubmittingStorage(true)
    try {
      // allSettled so one backend failing doesn't stop the others from proceeding
      // Failed transfers are intentionally ignored here -- they show up on the
      // maintenance progress page, not in this dialog
      await Promise.allSettled(
        storageServicesForHost.map(async (service) => {
          const disableResult = await handleVolumeMaintenanceToggle(service?.host)
          if (!disableResult?.success) {
            return false
          }
          // Both ids are the full service ids (host@backend)
          const targetHostId = params.transferTargets?.[service?.host]
          if (targetHostId) {
            await initiateVolumeHostTransfer({
              sourceHostId: service?.host,
              targetHostId,
              dryRun: false,
            })
          }
          return true
        }),
      )
    } finally {
      setSubmittingStorage(false)
    }
  }, [storageServicesForHost, handleVolumeMaintenanceToggle, params.transferTargets])

  // EMM-FR-009: Tag, disable the hypervisor and migrate the host's VMs
  const triggerMigrations = useCallback(async () => {
    setSubmittingMigrations(true)

    const migrationId = uuid()

    handleMaintenanceToggle(migrationId)

    const validVms = vmsOnHost.filter((vm) => {
      // Skip powered off VMs if flag toggled
      return params.skipPoweredOffVms
        ? ['active', 'paused', 'suspended'].includes(vm['OS-EXT-STS:vm_state'])
        : VALID_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
    })

    const tagPromises = validVms.map((vm) => simpleAddVmTag({ id: vm.id, tag: migrationId }))
    const tagResults = await Promise.allSettled(tagPromises)
    // If any VMs were not tagged properly and they are migrated off the host, then will lose history that they were part of the migration.
    // const failedTagIndexes = tagResults
    //   ?.map((result, idx) => (result.status === 'rejected' ? idx : null))
    //   ?.filter((idx) => idx !== null)
    // const failedTagVms = failedTagIndexes.map((idx) => validVms[idx])
    // Potentially show something regarding the VMs that didn't get tagged

    const promises = validVms.map((vm) => {
      const body = VALID_COLD_MIGRATION_STATES.includes(vm['OS-EXT-STS:vm_state'])
        ? {
            migrate: {
              host: params.selectHost ? params.host[0]?.id : null,
            },
          }
        : {
            'os-migrateLive': {
              host: params.selectHost ? params.host[0]?.id : null,
              block_migration: 'auto',
            },
          }
      return simpleMigrateVirtualMachine({ id: vm.id, body })
    })
    const results = await Promise.allSettled(promises)
    const acceptedMigrationIndexes = results
      ?.map((result, idx) => (result.status === 'fulfilled' ? idx : null))
      ?.filter((idx) => idx !== null)
    // If we need to show which VMs were successfully triggered in the future
    // const vmsWithMigrationTriggered = acceptedMigrationIndexes.map((idx) => validVms[idx])
  }, [params, host, vmsOnHost, handleMaintenanceToggle])

  // EMM-FR-011: Closing resets errors and reports success when told
  const handleClose = (success?: boolean) => {
    resetDisableHypervisor()
    resetDisableStorage()
    onClose(success)
  }

  const includesVms =
    isHypervisor &&
    [MigrationOptions.VmsAndStorage, MigrationOptions.VmsOnly].includes(params.migrationOption)
  const includesStorage =
    isStorageNode &&
    [MigrationOptions.VmsAndStorage, MigrationOptions.StorageOnly].includes(params.migrationOption)

  // EMM-FR-008, EMM-FR-009, EMM-FR-010: Run storage then VM maintenance and redirect
  const handleSubmit = useCallback(async () => {
    if (includesStorage) {
      // Failures are surfaced via the form error but don't block the VM migrations or redirect
      await handleStorageMaintenance()
    }
    if (includesVms) {
      await (vmsOnHost.length ? triggerMigrations() : handleMaintenanceToggle(uuid()))
    } else {
      handleClose(true)
    }
    // Always land on the host details page, whichever resources were migrated
    history.push(routeManager.routes.openstack.hostDetails.path({ id: host?.id }))
  }, [
    includesStorage,
    includesVms,
    handleStorageMaintenance,
    vmsOnHost.length,
    triggerMigrations,
    handleMaintenanceToggle,
    history,
    host,
  ])

  return (
    <ModalForm
      title={`Enable Maintenance Mode - ${host?.name}`}
      onSubmit={handleSubmit}
      onClose={() => (submittingMigrations ? setOpenConfirmation(true) : handleClose())}
      loading={loadingVms || loadingImages}
      submitting={
        disablingHypervisor || disablingStorage || submittingStorage || submittingMigrations
      }
      error={errorDisablingHypervisor || errorDisablingStorage}
      submitTitle={'Migrate Resources and Enable Maintenance Mode'}
      panel="drawer"
      maxWidth={1200}
      // disableSubmit={!allowMaintenance}
      open
      onBackdropClick={() => {
        submittingMigrations ? setOpenConfirmation(true) : handleClose()
      }}
    >
      <ConfirmationDialog
        open={openConfirmation}
        text="Migrations in progress. Are you sure you want to cancel?"
        onCancel={() => setOpenConfirmation(false)}
        onConfirm={handleClose}
      />
      <div className={classes.fields}>
        <Text variant="body2">
          When in maintenance mode, the selected maintenance resources cannot be created or powered
          on. Additionally, Private Cloud Director will not schedule or migrate any of the selected
          resources to this host while it is in maintenance mode. Refer to{' '}
          <ExternalLink url={maintenanceModeDoc}>documentation</ExternalLink> for details.
        </Text>
        <RadioFields
          id="migrationOption"
          title="Migrate"
          options={getMigrationOptions({ isHypervisor, isStorageNode, isHypervisorAndStorageNode })}
          value={params.migrationOption}
          onChange={getParamsUpdater('migrationOption')}
          className={classes.migrationOptions}
        />
        {isHypervisor &&
          [MigrationOptions.VmsAndStorage, MigrationOptions.VmsOnly].includes(
            params.migrationOption,
          ) && (
            <Card title="Virtual Machines">
              <div className={classes.fields}>
                {isLastHypervisor && (
                  <Info error>
                    <Text variant="body2">
                      <FontAwesomeIcon className={classes.errorIcon}>
                        triangle-exclamation
                      </FontAwesomeIcon>
                      <b>WARNING:</b> You are about to put the only host in this cluster with
                      hypervisor role assigned in maintenance mode. Putting this host in maintenance
                      mode will have following side effects:
                    </Text>
                    <div className={classes.tabbed}>
                      <Text variant="body2">
                        <b>1.</b> VMs that are eligible for migration will be migrated off the
                        cluster.
                      </Text>
                      <Text variant="body2">
                        <b>2.</b> For remaining VMs, all VM operations on this cluster other than
                        editing VM metadata and migrating the VM off the cluster will fail.
                      </Text>
                    </div>
                  </Info>
                )}
                {vmsOnHost?.length ? (
                  <>
                    <Text variant="body2">
                      Private Cloud Director will disable scheduling, then attempt to migrate all
                      eligible VMs on this host to other hosts, or to the selected host. There are
                      currently <b>{vmsOnHost?.length}</b> VM(s) on this host.
                    </Text>
                    <Text variant="body2">
                      <b>NOTE:</b> If you have any active vTPM VMs, please ensure they meet the
                      prerequisites listed in{' '}
                      <ExternalLink url={vtpmMigrationDoc}>this article</ExternalLink> for live
                      migration.
                    </Text>
                    <ToggleSwitchField
                      id="skipPoweredOffVms"
                      label="Skip migration of powered off VMs"
                      value={params.skipPoweredOffVms}
                      onChange={getParamsUpdater('skipPoweredOffVms')}
                      customTooltipInfoBody={
                        <Text variant="body2" className={classes.switchTooltip}>
                          If enabled, powered off (stopped) VMs will not be migrated.
                        </Text>
                      }
                    />
                    <ToggleSwitchField
                      id="selectHost"
                      label="Designate a specific host to migrate VMs to"
                      value={params.selectHost}
                      onChange={getParamsUpdater('selectHost')}
                      customTooltipInfoBody={
                        <Text variant="body2" className={classes.switchTooltip}>
                          If enabled, select a host to which all active VMs on this host will be
                          migrated to. If left disabled, VMs will be distributed to new hosts
                          automatically.
                        </Text>
                      }
                    />
                  </>
                ) : (
                  <Text variant="body2">
                    There are no VMs on this host. Maintenance mode may be enabled, and scheduling
                    will be disabled on this host.
                  </Text>
                )}
                {params.selectHost && (
                  <div className={classes.horizontalScroll}>
                    <ListTableField
                      id="host"
                      data={validHosts}
                      loading={loadingResmgrHosts || loadingHypervisors}
                      columns={hostColumns}
                      onChange={getParamsUpdater('host')}
                      value={params.host}
                      uniqueIdentifier="id"
                      searchTargets={['name']}
                      noRowsPerPage
                      required
                      onReload={() => {
                        reload(true)
                        reloadHypervisors(true)
                      }}
                    />
                  </div>
                )}
              </div>
            </Card>
          )}
        {isStorageNode &&
          [MigrationOptions.VmsAndStorage, MigrationOptions.StorageOnly].includes(
            params.migrationOption,
          ) && (
            <Card title="Storage Volumes">
              <div className={classes.fields}>
                <Text variant="body2">
                  Volumes managed by this host will be reassigned to another host with the
                  Persistent Storage role and the same storage backend configured. Volume data stays
                  on the backend and attached VMs are not interrupted.
                </Text>
                <Text variant="caption1">Select a target host for each backend.</Text>
                <div className={classes.backendsTable}>
                  <Text variant="caption1">Backend</Text>
                  <Text variant="caption1">Volumes</Text>
                  <Text variant="caption1">Transfer To</Text>
                  {storageServicesForHost.map((service) => (
                    <div key={service.id} className={classes.backendRow}>
                      <div className={classes.backend}>
                        <Text variant="caption1">{service?.name}</Text>
                        <Text variant="body2" className={classes.hostCode}>
                          {service?.host}
                        </Text>
                      </div>
                      <div className={classes.verticalCenter}>
                        {volumesByCinderService[service?.host]?.length ? (
                          <SimpleLink
                            icon={
                              expandedBackends[service?.host] ? 'chevron-down' : 'chevron-right'
                            }
                            iconPosition="left"
                            onClick={() =>
                              setExpandedBackends((prev) => ({
                                ...prev,
                                [service?.host]: !prev[service?.host],
                              }))
                            }
                          >
                            {volumesByCinderService[service?.host].length} volumes
                          </SimpleLink>
                        ) : (
                          <Text variant="body2">0 volumes</Text>
                        )}
                      </div>
                      {getTransferTargetOptions(service).length ? (
                        <PicklistField
                          id={`transferTarget-${service?.host}`}
                          label=""
                          DropdownComponent={AsyncDropdown}
                          items={getTransferTargetOptions(service)}
                          value={params.transferTargets?.[service?.host]}
                          onChange={(value) => handleTransferTargetChange(service, value)}
                          required={!!volumesByCinderService[service?.host]?.length}
                        />
                      ) : (
                        <div className={classes.verticalCenter}>
                          <Badge variant="error" text="No Eligible Host" ellipsisAt={25} />
                        </div>
                      )}
                      {expandedBackends[service?.host] && (
                        <div className={classes.volumeList}>
                          <Grid
                            uniqueIdentifier="id"
                            data={getVolumesWithResult(service)}
                            columns={volumeListColumnsWithResult}
                            loading={false}
                            compact
                            disableToolbar
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
      </div>
    </ModalForm>
  )
}
